import React, { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, TooltipProps, ReferenceLine } from 'recharts'
import { useTranslation } from 'react-i18next'
import { Typography, Box, useTheme } from '@mui/material'
import { StyledWidgetDailyChromecastUsage } from './WidgetDailyChromecastUsage.styles'

interface DataItem {
  timestamp: number;
  value: number;
}

interface ChartDataItem {
  dayOfMonth: number;
  value: number;
  formattedDate: string;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{ value: number; payload: ChartDataItem }>;
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  const { t } = useTranslation();
  if (active && payload && payload.length && payload[0].value > 0) {
    const { value, formattedDate } = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          borderRadius: '8px',
          padding: '10px',
          border: '1px solid #ccc',
          height: '60px',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="body2">
          {formattedDate}
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {value} {t('statistics.chromecasts.used')}
        </Typography>
      </Box>
    );
  }
  return null;
}

interface WidgetDailyChromecastUsageProps {
  data: DataItem[];
  startDate: Date;
  endDate: Date;
}

const WidgetDailyChromecastUsage: React.FC<WidgetDailyChromecastUsageProps> = ({ data, startDate, endDate }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (data && data.length > 0) {
      return data;
    }
    return [];
  }, [data]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(i18n.language, { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    }).replace(/^\w/, (c) => c.toUpperCase());
  };

  const processedChartData = useMemo(() => {
    const dataMap = new Map(chartData.map(item => [new Date(item.timestamp).getDate(), item.value]));
    const daysInMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
    
    return Array.from({ length: daysInMonth }, (_, index) => {
      const dayOfMonth = index + 1;
      const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), dayOfMonth);
      return {
        dayOfMonth,
        value: dataMap.get(dayOfMonth) || 0,
        formattedDate: formatDate(currentDate)
      };
    });
  }, [chartData, startDate, endDate, i18n.language]);

  const maxValue = useMemo(() => {
    return Math.max(...processedChartData.map(item => item.value));
  }, [processedChartData]);

  const currentMonth = useMemo(() => {
    const monthYear = startDate.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  }, [startDate, i18n.language]);
  

  return (
    <StyledWidgetDailyChromecastUsage>
      <Box display="flex" alignItems="center" justifyContent="flex-start" gap={0.5} width="100%" mb={2}>
        <Typography variant="h6" fontWeight={500} color={theme.palette.content.primary} fontSize={'16px'} padding={'10px 20px'}>
          {t('statistics.chromecasts.daily_usage')} - {currentMonth}
        </Typography>
      </Box>
      <ResponsiveContainer width="100%" height="75%">
        <BarChart data={processedChartData}>
          <XAxis 
            dataKey="dayOfMonth" 
            tickFormatter={(day: number) => `${day}`}
            interval={0}
            tick={{ fontSize: 10, fill: theme.palette.content.tertiary }}
            axisLine={{ stroke: theme.palette.content.tertiary }}
            tickLine={false}
          />
          <YAxis 
            allowDecimals={false} 
            domain={[0, maxValue]}
            tickCount={5}
            tick={{ fontSize: 10, fill: theme.palette.content.tertiary }}
            axisLine={{ stroke: theme.palette.content.tertiary }}
            tickLine={false}
          />
          <Tooltip 
            content={<CustomTooltip />}
            cursor={{ fill: 'transparent' }}
          />
          <ReferenceLine y={maxValue} stroke={theme.palette.content.tertiary} strokeDasharray="3 3" />
          <Bar
            dataKey="value"
            fill="#59B5DA"
            activeBar={{ fill: '#2582A7' }} 
            radius={[6, 6, 0, 0]}
            barSize={16}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </StyledWidgetDailyChromecastUsage>
  );
};

export default WidgetDailyChromecastUsage;